import React from 'react';
import {
    FiGlobe,
    FiBriefcase,
    FiUsers,
    FiAward,
    
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { coreValues } from '../../data/Data';

const WhyUs = () => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3,
            },
        },
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.6, ease: [0.43, 0.13, 0.23, 0.96] }
        },
    };

    const cardVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.7, ease: [0.43, 0.13, 0.23, 0.96] }
        },
    };

    const iconVariants = {
        hidden: { scale: 0, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: {
                type: 'spring',
                stiffness: 260,
                damping: 20,
                delay: 0.2,
            },
        },
    };

    const fadeInUpVariants = {
        hidden: { y: 30, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.8, ease: 'easeOut' }
        },
    };

    const keyFeatures = [
        {
            icon: <FiBriefcase className="w-8 h-8" />,
            title: "Deep Expertise",
            description: "Benefit from our extensive knowledge and years of experience in international trade, migration, investment, and global business solutions.",
            gradient: "from-[#0f766e] to-[#0f766e]",
        },
        {
            icon: <FiGlobe className="w-8 h-8" />,
            title: "Extensive Global Network",
            description: "Leverage our vast network of trusted partners, agents, and resources across the globe for seamless international operations.",
            gradient: "from-[#3B82F6] to-[#3B82F6]",
        },
        {
            icon: <FiUsers className="w-8 h-8" />,
            title: "Personalized Solutions",
            description: "We develop customized solutions tailored to your specific goals, challenges, and circumstances for optimal outcomes.",
            gradient: "from-[#0F172A] to-[#0F172A]"
        },
        {
            icon: <FiAward className="w-8 h-8" />,
            title: "Proven Track Record",
            description: "Our history of successful client partnerships and achievements speaks to our commitment to excellence.",
            gradient: "from-[#735557] to-[#735557]",
        },
    ];

    return (
        <section className="py-20 bg-gradient-to-b from-white to-bgpalecolor font-basefont overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                {/* Header Section */}
                <motion.div
                    ref={ref}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="text-center mb-16"
                >
                    <motion.div variants={fadeInUpVariants} className="inline-block">
                        <span className="bg-basecolor bg-opacity-10 text-basecolor px-4 py-1 rounded-full text-sm font-medium uppercase tracking-wider">
                            Why Choose Us
                        </span>
                    </motion.div>
                    <motion.h2
                        variants={fadeInUpVariants}
                        className="text-4xl md:text-5xl font-bold text-basecolor mt-4 mb-6"
                    >
                        Your Global Success Partner
                    </motion.h2>
                    <motion.p
                        variants={fadeInUpVariants}
                        className="max-w-3xl mx-auto text-gray-700 text-lg"
                    >
                        At PG Overseas, we combine expertise, global reach, and personalized attention
                        to help you navigate the complexities of international business and migration.
                    </motion.p>
                </motion.div>

                {/* Key Features Cards */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20 relative overflow:visible -z-10"
                >
                    {keyFeatures?.map((feature, index) => (
                        <motion.div
                            key={index}
                            variants={cardVariants}
                            className={`bg-gradient-to-br ${feature.gradient} rounded-2xl shadow-xl p-6 text-white transform transition-all duration-300 hover:scale-105 hover:shadow-2xl`}
                            whileHover={{ y: -5 }}
                        >
                            <motion.div
                                variants={iconVariants}
                                className="w-16 h-16 rounded-full bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center mb-4"
                            >
                                {feature.icon}
                            </motion.div>
                            <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                            <p className="text-white text-opacity-90">{feature.description}</p>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Core Values Section */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="mb-16"
                >
                    <motion.div variants={fadeInUpVariants} className="text-center mb-12">
                        <h3 className="text-4xl md:text-5xl font-bold text-basecolor mb-4">Our Core Values</h3>
                        <p className="max-w-2xl mx-auto text-gray-700">
                            These principles guide everything we do and ensure we deliver exceptional service to our clients.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {coreValues?.map((value, index) => {
                            const Icon = value.icon;
                            return (
                                <motion.div
                                    key={index}
                                    variants={itemVariants}
                                    className="bg-white rounded-xl shadow-md p-6 border-l-4 border-basecolor hover:shadow-lg transition-shadow duration-300"
                                >
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0 mr-4">
                                            <div className="w-12 h-12 rounded-full bg-basecolor bg-opacity-10 flex items-center justify-center text-basecolor">
                                                <Icon className="w-6 h-6" />
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="text-lg font-semibold text-basecolor mb-2">{value.name}</h4>
                                            <p className="text-gray-700">{value.description}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Testimonial Highlight */}
                {/* <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={fadeInUpVariants}
                    className="bg-basecolor rounded-2xl p-8 text-white text-center mb-16"
                >
                    <div className="max-w-4xl mx-auto">
                        <FaHandshake className="w-12 h-12 mx-auto mb-6 opacity-80" />
                        <h3 className="text-2xl font-bold mb-6">Trusted by Businesses and Individuals Worldwide</h3>
                        <p className="text-xl italic mb-8">
                            "PG Overseas made our international expansion seamless. Their expertise and personalized approach exceeded our expectations at every step."
                        </p>
                        <div className="flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
                                <span className="text-basecolor font-bold">JD</span>
                            </div>
                            <div className="text-left">
                                <p className="font-semibold">John Doe</p>
                                <p className="text-sm opacity-80">CEO, Global Ventures</p>
                            </div>
                        </div>
                    </div>
                </motion.div> */}

                {/* CTA Section */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={fadeInUpVariants}
                    className="text-center"
                >
                    <h3 className="text-2xl font-bold text-basecolor mb-6">Ready to Start Your Global Journey?</h3>
                    <p className="max-w-2xl mx-auto text-gray-700 mb-8">
                        Contact us today to discuss how we can help you achieve your international goals.
                    </p>
                    <motion.a
                        href="/contact"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="inline-block bg-basecolor hover:bg-opacity-90 text-white font-bold py-3 px-8 rounded-full shadow-lg transition-all duration-300"
                    >
                        Get in Touch
                    </motion.a>
                </motion.div>
            </div>
        </section>
    );
};

export default WhyUs;
